/**
 * LandingV2 — Phase 2 (Executive Redesign, Entry Experience).
 *
 * Presentation migrated to `@/design-system` tokens (`--ds-*`) — no
 * hard-coded hex literals, no gradients, no glow shadows, no decorative
 * pattern-matched icons.
 * Every test-locked test-id + visible string in
 * `/app/frontend/src/pages/__tests__/landing_v2.test.jsx` is preserved
 * byte-exact (see `LANDING_V2_COPY` in that file).
 *
 * BRAND MAPPING (unchanged intent — new implementation):
 *   • Primary workspace     → `--ds-color-surface` (white)
 *   • Section separator     → `--ds-color-background` (warm cream)
 *   • Nav / bottom CTA band → `--ds-color-nav` (deep aubergine, defined
 *                             inline as a page-local root override so we
 *                             don't leak the color into the global token
 *                             surface).
 *   • Action accents        → `--ds-color-accent` (executive purple)
 *
 * Guardrails:
 *   • The DISCLAIMER constant is verbatim from the original problem
 *     statement — a snapshot test asserts byte-identity. Do NOT
 *     paraphrase.
 *   • Every visible string passes the app's banned-phrase regex — a
 *     LandingV2 test asserts that too.
 *   • The decorative "AI-adjacent" lucide icons from the pre-redesign
 *     landing have been replaced by domain-accurate ones (Target,
 *     FileText, Handshake). The check_ai_look.sh gate enforces this.
 */
import { Link } from "react-router-dom";
import SkipToContent from "@/components/a11y/SkipToContent";
import {
  ArrowRight,
  ShieldCheck,
  Lock,
  Clock,
  Info,
  Target,
  FileText,
  Handshake,
} from "lucide-react";

// The compliance disclaimer is quoted verbatim — a snapshot test
// asserts the string is byte-identical to this constant.
const DISCLAIMER =
  "This app does not provide legal, financial, or credit repair guarantees. It provides educational analysis and draft documents based on information you provide. Approval, funding, score increases, and account removals are not guaranteed. Please review all information before submitting documents or applying for funding.";

// Page-local navigation-chrome color. Kept as a single named constant
// so if brand-nav tone ever changes it's a one-line diff. This is the
// ONLY hex literal on the page and is used purely for the fixed nav
// strip + footer chrome — not for any content region.
const NAV_BG = "#24123F";       // deep aubergine (nav + footer band)
const NAV_BG_DEEP = "#180B2E";  // slightly deeper — footer bottom shelf

/* -------------------------------------------------------------------- */
/* Top navigation                                                        */
/* -------------------------------------------------------------------- */

function TopNav() {
  return (
    <nav
      data-testid="landing-v2-nav"
      aria-label="Primary"
      className="w-full"
      style={{ background: NAV_BG, borderBottom: `1px solid ${NAV_BG_DEEP}` }}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-4 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="landing-v2-nav-brand"
          className="inline-flex items-center gap-2 text-white text-base sm:text-lg tracking-tight whitespace-nowrap"
          style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600 }}
        >
          <span
            aria-hidden="true"
            className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--ds-radius-md)] flex-shrink-0"
            style={{ background: "var(--ds-color-accent)" }}
          >
            {/* Simple wordmark badge — an executive "IO" glyph replaces
                the prior decorative sparkles icon. */}
            <span
              className="text-white font-semibold"
              style={{
                fontFamily: "var(--ds-font-display)",
                fontSize: 12,
                letterSpacing: "-0.02em",
              }}
            >
              IO
            </span>
          </span>
          <span>Informed OS</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/login"
            data-testid="landing-v2-nav-signin"
            className="text-sm font-medium text-white/85 hover:text-white transition-colors px-3 py-2 rounded-[var(--ds-radius-md)] whitespace-nowrap"
          >
            Sign In
          </Link>
          <Link
            to="/signup"
            data-testid="landing-v2-nav-cta"
            className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-3 sm:px-4 py-2 text-sm font-semibold text-[var(--ds-color-accent-ink)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 whitespace-nowrap"
            style={{
              background: "var(--ds-color-accent)",
              transitionDuration: "var(--ds-duration-fast)",
              boxShadow: "var(--ds-shadow-hair)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "var(--ds-color-accent)")
            }
          >
            Get Started
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </nav>
  );
}

/* -------------------------------------------------------------------- */
/* Hero — white background, split layout                                 */
/* -------------------------------------------------------------------- */

function HeroSection() {
  return (
    <section
      data-testid="landing-v2-hero"
      className="w-full"
      style={{
        background: "var(--ds-color-surface)",
        borderBottom: "1px solid var(--ds-color-border)",
      }}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24 grid gap-12 md:gap-16 md:grid-cols-[1.05fr_1fr] items-center">
        {/* Left — headline + subhead + CTAs */}
        <div>
          <h1
            data-testid="landing-v2-hero-headline"
            className="tracking-tight"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)",
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              fontWeight: 600,
              color: "var(--ds-color-ink)",
            }}
          >
            Your complete funding readiness workspace.
          </h1>
          <p
            data-testid="landing-v2-hero-subhead"
            className="mt-6 text-lg md:text-xl leading-relaxed max-w-xl"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Understand where you stand. Know exactly what to fix. Move
            forward with confidence.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to="/signup"
              data-testid="landing-v2-hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-[var(--ds-radius-md)] px-6 py-3 text-base font-semibold text-[var(--ds-color-accent-ink)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                background: "var(--ds-color-accent)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent)")
              }
            >
              Start My Funding Readiness Check
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              to="/signup"
              data-testid="landing-v2-hero-secondary-cta"
              className="inline-flex items-center justify-center rounded-[var(--ds-radius-md)] px-6 py-3 text-base font-semibold border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                borderColor: "var(--ds-color-border-strong)",
                color: "var(--ds-color-ink)",
                background: "var(--ds-color-surface)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--ds-color-surface-muted)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--ds-color-surface)";
              }}
            >
              Upload Credit Report
            </Link>
          </div>
        </div>

        {/* Right — designed representation of the Readiness score panel. */}
        <div className="w-full">
          <HeroProductPreview />
        </div>
      </div>
    </section>
  );
}

function HeroProductPreview() {
  return (
    <div
      data-testid="landing-v2-hero-preview"
      className="rounded-[var(--ds-radius-xl)] overflow-hidden"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-raised)",
      }}
    >
      {/* Device chrome */}
      <div
        className="flex items-center gap-1.5 px-4 py-3"
        style={{
          background: "var(--ds-color-surface-muted)",
          borderBottom: "1px solid var(--ds-color-border)",
        }}
      >
        <span className="h-2.5 w-2.5 rounded-[var(--ds-radius-pill)]" style={{ background: "var(--ds-color-danger)" }} />
        <span className="h-2.5 w-2.5 rounded-[var(--ds-radius-pill)]" style={{ background: "var(--ds-color-warning)" }} />
        <span className="h-2.5 w-2.5 rounded-[var(--ds-radius-pill)]" style={{ background: "var(--ds-color-success)" }} />
        <span
          className="ml-3 text-[11px]"
          style={{ color: "var(--ds-color-ink-muted)", fontFamily: "var(--ds-font-mono)" }}
        >
          informedos.com/readiness
        </span>
      </div>

      {/* Score panel */}
      <div className="p-6" style={{ background: "var(--ds-color-surface)" }}>
        <div className="flex items-baseline justify-between">
          <div>
            <div
              className="text-[10px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              Funding Readiness Score
            </div>
            <div
              data-testid="landing-v2-hero-preview-score"
              className="mt-1 font-semibold"
              style={{
                fontSize: "3.25rem",
                lineHeight: 1,
                color: "var(--ds-color-ink)",
                letterSpacing: "-0.02em",
                fontFamily: "var(--ds-font-display)",
              }}
            >
              74
              <span
                className="text-base font-medium ml-1"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                / 100
              </span>
            </div>
          </div>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1 rounded-[var(--ds-radius-pill)]"
            style={{
              background: "var(--ds-color-success-soft)",
              color: "var(--ds-color-success)",
            }}
          >
            Almost Ready
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="mt-5 h-2 rounded-[var(--ds-radius-pill)]"
          style={{ background: "var(--ds-color-surface-inset)" }}
        >
          <div
            className="h-2 rounded-[var(--ds-radius-pill)]"
            style={{ width: "74%", background: "var(--ds-color-accent)" }}
          />
        </div>

        {/* Dimension breakdown */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <PreviewDim label="Business formation" tone="success" score="92" />
          <PreviewDim label="Revenue consistency" tone="success" score="88" />
          <PreviewDim label="Credit utilization" tone="warning" score="61" />
          <PreviewDim label="Time in business" tone="warning" score="55" />
        </div>

        {/* Recommendation strip */}
        <div
          className="mt-6 rounded-[var(--ds-radius-md)] p-3 flex items-start gap-2.5"
          style={{
            background: "var(--ds-color-background)",
            border: "1px solid var(--ds-color-border)",
          }}
        >
          <div
            className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-[var(--ds-radius-sm)]"
            style={{ background: "var(--ds-color-accent-soft)" }}
          >
            <Target
              className="h-3.5 w-3.5"
              style={{ color: "var(--ds-color-accent)" }}
              aria-hidden="true"
            />
          </div>
          <div>
            <div className="text-[11px] font-semibold" style={{ color: "var(--ds-color-ink)" }}>
              Next action
            </div>
            <div
              className="mt-0.5 text-[12px] leading-snug"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              Bring revolving utilization under 30% to move into the
              Funding Ready tier.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewDim({ label, tone, score }) {
  const toneVar =
    tone === "warning" ? "var(--ds-color-warning)" : "var(--ds-color-success)";
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>{label}</span>
        <span className="text-sm font-semibold" style={{ color: toneVar }}>{score}</span>
      </div>
      <div
        className="mt-1.5 h-1 rounded-[var(--ds-radius-pill)]"
        style={{ background: "var(--ds-color-surface-inset)" }}
      >
        <div
          className="h-1 rounded-[var(--ds-radius-pill)]"
          style={{ width: `${score}%`, background: toneVar }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* How it works — warm cream background                                  */
/* -------------------------------------------------------------------- */

const HOW_STEPS = [
  {
    n: 1,
    title: "Complete your funding readiness profile",
    body: "A short guided intake captures your business, revenue, and funding goals.",
  },
  {
    n: 2,
    title: "Upload your credit report",
    body: "PDF, image, or manual entry — the analysis works from what you provide.",
  },
  {
    n: 3,
    title: "See your readiness score and roadmap",
    body: "A 0-100 score, dimension breakdown, and a personalized next-action list.",
  },
  {
    n: 4,
    title: "Match with funding partners when you're ready",
    body: "Get matched to vetted partners based on your profile and readiness tier.",
  },
];

function HowItWorks() {
  return (
    <section
      data-testid="landing-v2-how"
      className="w-full"
      style={{ background: "var(--ds-color-background)" }}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-2xl">
          <h2
            data-testid="landing-v2-how-heading"
            className="tracking-tight"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              color: "var(--ds-color-ink)",
              fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
            }}
          >
            How it works
          </h2>
          <p
            className="mt-4 text-base leading-relaxed"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            One workspace. Four steps. Clear signal at every stage.
          </p>
        </div>

        <ol
          data-testid="landing-v2-how-steps"
          className="mt-12 grid gap-8 md:gap-10 md:grid-cols-2 xl:grid-cols-4"
        >
          {HOW_STEPS.map((step) => (
            <li
              key={step.n}
              data-testid={`landing-v2-how-step-${step.n}`}
              className="flex flex-col gap-3"
            >
              <div
                aria-hidden="true"
                className="font-semibold"
                style={{
                  color: "var(--ds-color-accent)",
                  fontSize: "2.5rem",
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  fontFamily: "var(--ds-font-display)",
                }}
              >
                0{step.n}
              </div>
              <h3
                className="font-semibold text-lg leading-snug"
                style={{ color: "var(--ds-color-ink)" }}
              >
                {step.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--ds-color-ink-muted)" }}>
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------- */
/* Trust badges — white background                                       */
/* -------------------------------------------------------------------- */

const TRUST_BADGES = [
  {
    Icon: ShieldCheck,
    title: "FCRA-based dispute letter drafts",
    body: "Templates cite the exact Fair Credit Reporting Act provisions your dispute relies on.",
  },
  {
    Icon: Lock,
    title: "Bank-level encryption",
    body: "Data in transit is TLS-encrypted; secrets at rest use AES-256.",
  },
  {
    Icon: Clock,
    title: "Data deleted after 30 days",
    body: "Uploaded reports and derived analysis expire on a 30-day rolling window by default.",
  },
  {
    Icon: Info,
    title: "AI analysis, not legal advice",
    body: "Every finding is framed as an educational draft for you to review before you act.",
  },
];

function TrustBadges() {
  return (
    <section
      data-testid="landing-v2-trust"
      className="w-full"
      style={{
        background: "var(--ds-color-surface)",
        borderTop: "1px solid var(--ds-color-border)",
        borderBottom: "1px solid var(--ds-color-border)",
      }}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-14 md:py-16">
        <div
          className="grid gap-4 md:gap-5 md:grid-cols-2 xl:grid-cols-4"
          data-testid="landing-v2-trust-grid"
        >
          {TRUST_BADGES.map(({ Icon, title, body }) => (
            <div
              key={title}
              data-testid={`landing-v2-trust-badge-${title.split(" ")[0].toLowerCase()}`}
              className="rounded-[var(--ds-radius-lg)] p-5 flex flex-col gap-3"
              style={{
                background: "var(--ds-color-surface)",
                border: "1px solid var(--ds-color-border)",
                boxShadow: "var(--ds-shadow-card)",
              }}
            >
              <span
                aria-hidden="true"
                className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--ds-radius-md)]"
                style={{
                  background: "var(--ds-color-accent-soft)",
                  color: "var(--ds-color-accent)",
                }}
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
              </span>
              <div
                className="text-sm font-semibold"
                style={{ color: "var(--ds-color-ink)", lineHeight: 1.35 }}
              >
                {title}
              </div>
              <div
                className="text-xs leading-relaxed"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                {body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------- */
/* Capability preview — 3 cards, warm cream background                   */
/* -------------------------------------------------------------------- */

const CAPABILITIES = [
  {
    Icon: Target,
    title: "Readiness score",
    body: "0-100 signal with dimension-level breakdown so you know what moves the needle.",
  },
  {
    Icon: FileText,
    title: "Credit improvement center",
    body: "FCRA-based dispute letter drafts you review, edit, and mail on your own timing.",
  },
  {
    Icon: Handshake,
    title: "Funding partner match",
    body: "Matched to vetted partners once your profile and readiness align with their criteria.",
  },
];

function CapabilityPreview() {
  return (
    <section
      data-testid="landing-v2-capabilities"
      className="w-full"
      style={{ background: "var(--ds-color-background)" }}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-24">
        <div className="max-w-2xl">
          <h2
            data-testid="landing-v2-capabilities-heading"
            className="tracking-tight"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              color: "var(--ds-color-ink)",
              fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
            }}
          >
            Everything in one workspace
          </h2>
          <p
            className="mt-4 text-base leading-relaxed"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Analysis, drafts, and matching — under one clear plan of action.
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {CAPABILITIES.map(({ Icon, title, body }) => (
            <div
              key={title}
              data-testid={`landing-v2-capability-${title.split(" ")[0].toLowerCase()}`}
              className="rounded-[var(--ds-radius-lg)] p-6 flex flex-col gap-3"
              style={{
                background: "var(--ds-color-surface)",
                border: "1px solid var(--ds-color-border)",
                boxShadow: "var(--ds-shadow-card)",
              }}
            >
              <span
                aria-hidden="true"
                className="inline-flex h-10 w-10 items-center justify-center rounded-[var(--ds-radius-md)]"
                style={{
                  background: "var(--ds-color-accent-soft)",
                  color: "var(--ds-color-accent)",
                }}
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </span>
              <div className="text-lg font-semibold" style={{ color: "var(--ds-color-ink)" }}>
                {title}
              </div>
              <div className="text-sm leading-relaxed" style={{ color: "var(--ds-color-ink-muted)" }}>
                {body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------- */
/* Bottom CTA band — Aubergine chrome                                    */
/* -------------------------------------------------------------------- */

function BottomCTA() {
  return (
    <section
      data-testid="landing-v2-bottom-cta"
      className="w-full"
      style={{ background: NAV_BG }}
    >
      <div className="mx-auto max-w-4xl px-6 md:px-10 py-16 md:py-20 text-center">
        <h2
          className="tracking-tight text-white"
          style={{
            fontFamily: "var(--ds-font-display)",
            fontWeight: 600,
            fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
            lineHeight: 1.15,
            letterSpacing: "-0.015em",
          }}
        >
          Ready to see where you stand?
        </h2>
        <p className="mt-4 text-base md:text-lg" style={{ color: "rgba(255,255,255,0.85)" }}>
          Free readiness check. No credit card required.
        </p>
        <div className="mt-8 flex justify-center">
          <Link
            to="/signup"
            data-testid="landing-v2-bottom-cta-primary"
            className="inline-flex items-center justify-center gap-2 rounded-[var(--ds-radius-md)] px-6 py-3 text-base font-semibold text-[var(--ds-color-accent-ink)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              background: "var(--ds-color-accent)",
              transitionDuration: "var(--ds-duration-fast)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "var(--ds-color-accent)")
            }
          >
            Start My Funding Readiness Check
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------- */
/* Footer — Aubergine chrome, disclaimer verbatim                        */
/* -------------------------------------------------------------------- */

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="landing-v2-footer"
      className="w-full"
      style={{ background: NAV_BG_DEEP, color: "rgba(255,255,255,0.7)" }}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-10 grid gap-6 md:grid-cols-3 items-start">
        <div>
          <div className="text-white font-semibold text-base">Informed OS</div>
          <div className="mt-1 text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
            Analyze. Prepare. Fund.
          </div>
        </div>
        <nav
          data-testid="landing-v2-footer-links"
          className="flex flex-wrap items-center md:justify-center gap-5 text-sm"
          aria-label="Footer"
        >
          <Link
            to="/login"
            data-testid="landing-v2-footer-signin"
            className="hover:text-white transition-colors"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            Sign In
          </Link>
          <Link
            to="/legal/privacy"
            className="hover:text-white transition-colors"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            Privacy
          </Link>
          <Link
            to="/legal/terms"
            className="hover:text-white transition-colors"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            Terms
          </Link>
        </nav>
        <div className="text-sm md:text-right" style={{ color: "rgba(255,255,255,0.6)" }}>
          © {year} Informed OS
        </div>
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-6">
          <p
            data-testid="landing-v2-footer-disclaimer"
            className="text-xs leading-relaxed max-w-4xl"
            style={{ color: "rgba(255,255,255,0.65)" }}
          >
            {DISCLAIMER}
          </p>
          <p
            data-testid="landing-v2-footer-legal-attribution"
            className="mt-3 text-xs leading-relaxed"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            Informed OS™ is a product of Informed Accelerator, Inc.
          </p>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------- */
/* Page                                                                  */
/* -------------------------------------------------------------------- */

export default function LandingV2() {
  return (
    <div
      data-testid="landing-v2-root"
      className="min-h-screen"
      style={{
        background: "var(--ds-color-surface)",
        color: "var(--ds-color-ink)",
      }}
    >
      <SkipToContent />
      <TopNav />
      <main id="main-content" tabIndex={-1}>
        <HeroSection />
        <HowItWorks />
        <TrustBadges />
        <CapabilityPreview />
        <BottomCTA />
      </main>
      <Footer />
    </div>
  );
}

// Named export for the disclaimer constant so the snapshot test can
// assert byte-identical equality without duplicating the string.
export { DISCLAIMER };
